import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Skeleton } from "../ui/skeleton";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Paperclip, Reply, Trash2, Undo2, MailOpen, Mail, XCircle, CheckCheck, UserCheck } from "lucide-react";
import { WaitBadge } from "./MailWorkflow";

export const fmtWhen = (iso) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const mailBulk = (ids, action) => api.post("/staff/mail-bulk", { ids, action });

const BulkBar = ({ folder, ids, all, toggleAll, done }) => {
  const run = (action, msg) => {
    if (action === "delete" && !window.confirm(`Permanently delete ${ids.length} email${ids.length > 1 ? "s" : ""}? This can't be undone.`)) return;
    mailBulk(ids, action).then(() => { toast.success(msg); done(); }).catch((e) => toast.error(errText(e)));
  };
  const n = ids.length;
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex-wrap" data-testid="mail-bulk-bar">
      <Checkbox checked={all ? true : n ? "indeterminate" : false} onCheckedChange={toggleAll} aria-label="Select all" data-testid="mail-select-all" />
      <span className="text-xs text-slate-500 mr-2" data-testid="mail-selected-count">{n ? `${n} selected` : "Select all"}</span>
      {n > 0 && (folder === "trash" ? (
        <>
          <Button size="sm" variant="outline" onClick={() => run("restore", "Restored")} data-testid="mail-bulk-restore" className="h-8 gap-1.5"><Undo2 size={14} /> Restore</Button>
          <Button size="sm" variant="outline" onClick={() => run("delete", "Deleted forever")} data-testid="mail-bulk-delete" className="h-8 gap-1.5 text-red-600"><XCircle size={14} /> Delete forever</Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={() => run("trash", `Moved ${n} to trash`)} data-testid="mail-bulk-trash" className="h-8 gap-1.5 text-red-600"><Trash2 size={14} /> Delete</Button>
          {folder === "inbox" && <>
            <Button size="sm" variant="outline" onClick={() => run("done", "Marked as done")} data-testid="mail-bulk-done" className="h-8 gap-1.5"><CheckCheck size={14} /> Mark done</Button>
            <Button size="sm" variant="outline" onClick={() => run("read", "Marked read")} data-testid="mail-bulk-read" className="h-8 gap-1.5"><MailOpen size={14} /> Mark read</Button>
            <Button size="sm" variant="outline" onClick={() => run("unread", "Marked unread")} data-testid="mail-bulk-unread" className="h-8 gap-1.5"><Mail size={14} /> Mark unread</Button>
          </>}
        </>
      ))}
    </div>
  );
};

const Row = ({ m, folder, checked, onCheck, onOpen }) => {
  const unread = m.direction === "in" && !m.read;
  const who = folder === "sent" || m.direction === "out" ? `To: ${m.to.join(", ")}` : (m.from_name || m.from);
  return (
    <li className={`flex items-start transition-colors hover:bg-slate-50 ${checked ? "bg-[#FFF4F0]" : unread ? "bg-[#FFF9F6]" : ""}`}>
      <span className="pl-4 pt-3.5"><Checkbox checked={checked} onCheckedChange={onCheck} aria-label="Select email" data-testid={`mail-check-${m.id}`} /></span>
      <button onClick={() => onOpen(m.id)} data-testid={`mail-item-${m.id}`} className="flex-1 min-w-0 text-left pl-3 pr-4 py-3 flex gap-3 items-start">
        <span className={`mt-2 h-2 w-2 rounded-full shrink-0 ${unread ? "bg-[#F5551E]" : "bg-transparent"}`} />
        <span className="flex-1 min-w-0">
          <span className="flex items-center justify-between gap-3">
            <span className={`truncate text-sm ${unread ? "font-bold text-[#12234A]" : "text-slate-700"}`}>{who}</span>
            <span className="text-xs text-slate-400 shrink-0 flex items-center gap-1.5">
              <WaitBadge m={m} testid={`mail-wait-${m.id}`} />
              {m.assigned_to && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#12234A] bg-[#12234A]/5 rounded-full px-1.5 py-0.5" data-testid={`mail-assigned-${m.id}`}><UserCheck size={10} /> {m.assigned_name}</span>}
              {m.replied_at && <Reply size={12} />}{m.attachments?.length > 0 && <Paperclip size={12} />}{fmtWhen(m.created_at)}
            </span>
          </span>
          <span className={`block truncate text-sm ${unread ? "font-semibold text-slate-800" : "text-slate-600"}`}>{m.subject || "(no subject)"}</span>
          <span className="block truncate text-xs text-slate-400">{m.mailboxes?.join(", ")} · {m.snippet}</span>
        </span>
      </button>
    </li>
  );
};

export const MailList = ({ data, folder, onOpen, onChanged }) => {
  const [sel, setSel] = useState([]);
  useEffect(() => { setSel([]); }, [data, folder]);
  if (!data) return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;
  if (!data.items.length) {
    return <div data-testid="mail-empty" className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 text-sm">No email here yet.</div>;
  }
  const ids = data.items.map((m) => m.id);
  const all = sel.length === ids.length;
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <BulkBar folder={folder} ids={sel} all={all} toggleAll={() => setSel(all ? [] : ids)} done={() => { setSel([]); onChanged(); }} />
      <ul className="divide-y divide-slate-100" data-testid="mail-list">
        {data.items.map((m) => <Row key={m.id} m={m} folder={folder} checked={sel.includes(m.id)} onCheck={() => toggle(m.id)} onOpen={onOpen} />)}
      </ul>
    </div>
  );
};
